import {
  TINKOFF_CONTAINER_CLASSNAME,
  TINKOFF_ICON_CLASSNAME,
  TINKOFF_INPUT_CLASSNAME,
  TINKOFF_WL_HOST_CLASSNAME
} from '@components/AuthForm';
import {
  AUTH_TABS,
  type AvailableAuthMethods
} from '@components/AuthForm/authProviders';
import AuthFormBtn from '@components/AuthForm/components/AuthFormBtn';
import AuthFormPolicyDisclaimer from '@components/AuthForm/components/AuthFormPolicyDisclaimer';
import { OldInput } from '@ui';
import { twcls } from '@utils/twcls';
import { observer } from 'mobx-react';
import type { FC, FormEventHandler, KeyboardEvent, MouseEvent } from 'react';
import { useEffect } from 'react';
import maxLogo from '@images/auth/max-logo.png';
import maxLogo2x from '@images/auth/max-logo@2x.png';
import maxLogo3x from '@images/auth/max-logo@3x.png';

interface MainProps {
  currentTab: number;
  onSubmit: FormEventHandler<HTMLFormElement>;
  changeTab: (tab: number) => void;
  setInputData: (data: { value: string; error?: string }) => void;
  formData: { text: { value: string; error?: string } };
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onRegisterByEmailClickHandler: () => void;
  onBtnClickHandler: (
    e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>
  ) => void;
  onTelegramClick: () => void;
  onMaxClick?: () => void;
  btnTitle: string;
  userExists: boolean;
  loginErrorMsg: string;
  isShowTest: boolean;
  onLoginOrRegisterError: string;
  authMethods: AvailableAuthMethods;
  reinitializeTinkoff?: () => void;
}

const Main: FC<MainProps> = ({
  currentTab,
  onSubmit,
  changeTab,
  setInputData,
  formData,
  handleKeyDown,
  onBtnClickHandler,
  onTelegramClick,
  onMaxClick,
  onRegisterByEmailClickHandler,
  btnTitle,
  userExists,
  loginErrorMsg,
  onLoginOrRegisterError,
  authMethods,
  reinitializeTinkoff
}) => {
  const visibleTabs = authMethods.form.reduce((tabs, method) => {
    if (method.role !== 'tab') {
      return tabs;
    }
    const tab = AUTH_TABS.find(item => item.id === method.id);
    if (tab) {
      tabs.push(tab);
    }
    return tabs;
  }, [] as typeof AUTH_TABS);
  const isSmsTab = currentTab === 0;
  const isActionDisabled = Number(formData?.text?.value?.length) === 0;
  const showTelegram =
    userExists &&
    isSmsTab &&
    authMethods.form.some(method => method.id === 'telegram');
  const tinkoffEnabled =
    userExists && authMethods.redirects.includes('tinkoff');
  const showPartnerLogin = userExists && authMethods.redirects.length > 0;

  useEffect(() => {
    if (!tinkoffEnabled || !reinitializeTinkoff) {
      return;
    }
    let observer: MutationObserver | null = null;
    const frame = window.requestAnimationFrame(() => {
      reinitializeTinkoff();
      const containers = Array.from(
        document.querySelectorAll(
          `.${TINKOFF_CONTAINER_CLASSNAME}, .${TINKOFF_ICON_CLASSNAME}`
        )
      );
      if (containers.length === 0) {
        return;
      }
      const labelControl = (): void => {
        let pending = false;
        containers.forEach(container => {
          const control = container.querySelector('button, a');
          if (!(control instanceof HTMLElement)) {
            pending = true;
            return;
          }
          control.setAttribute('aria-label', 'Войти через Т-Банк');
        });
        if (!pending && observer) {
          observer.disconnect();
          observer = null;
        }
      };
      observer = new MutationObserver(labelControl);
      containers.forEach(container => {
        observer?.observe(container, { childList: true });
      });
      labelControl();
    });
    return () => {
      window.cancelAnimationFrame(frame);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [tinkoffEnabled, isSmsTab, reinitializeTinkoff]);

  return (
    <form autoComplete='on' onSubmit={onSubmit}>
      <div className='AuthForm__heading'>Вход или регистрация</div>
      {visibleTabs.length > 0 && (
        <div
          className='SettingsHeader'
          style={{
            gridTemplateColumns: `repeat(${visibleTabs.length}, 1fr)`
          }}
        >
          {visibleTabs.map(tab => (
            <div
              key={tab.id}
              className={`SettingsHeader__tab${
                currentTab === tab.index ? ' SettingsHeader__tab--active' : ''
              }`}
              onClick={() => {
                changeTab(tab.index);
              }}
            >
              {tab.label}
            </div>
          ))}
        </div>
      )}

      {loginErrorMsg && (
        <div className='AuthForm__error-msg'>{loginErrorMsg}</div>
      )}
      {onLoginOrRegisterError && (
        <div className='AuthForm__error-msg'>{onLoginOrRegisterError}</div>
      )}
      <OldInput
        key={currentTab === 2 ? 'email' : 'phone'}
        className='mb-6 text-center'
        inputClassName={currentTab === 0 ? TINKOFF_INPUT_CLASSNAME : ''}
        placeholder={currentTab === 2 ? 'Email' : 'Телефон'}
        getInputData={setInputData}
        field={currentTab === 2 ? 'text' : 'phone'}
        value={formData.text.value}
        error={formData.text.error}
        name='email-pass'
        onKeyDown={handleKeyDown}
        autoComplete='tel'
        autoFocus
      />
      {!userExists && (
        <AuthFormBtn
          disabled={isActionDisabled}
          onClick={onRegisterByEmailClickHandler}
          className='AuthFormBtn--grey'
        >
          Регистрация
        </AuthFormBtn>
      )}
      {tinkoffEnabled && (
        <div
          className={TINKOFF_WL_HOST_CLASSNAME}
          style={{
            position: 'absolute',
            visibility: 'hidden',
            width: 0,
            height: 0,
            overflow: 'hidden'
          }}
          aria-hidden='true'
        />
      )}
      {userExists && (
        <AuthFormBtn
          key={currentTab === 0 ? 'sms' : currentTab}
          disabled={isActionDisabled}
          onClick={onBtnClickHandler}
          className={twcls(
            showTelegram && 'AuthFormBtn--gap',
            showPartnerLogin && !showTelegram && 'AuthFormBtn--last'
          )}
        >
          {btnTitle}
        </AuthFormBtn>
      )}
      {/* {userExists && showTinkoffLabel && (
        <div
          className={twcls(
            'AuthForm__tbank-label',
            showTelegram && 'AuthForm__tbank-label--gap',
            showPartnerLogin && !showTelegram && 'AuthForm__tbank-label--last'
          )}
        >
          <div className={TINKOFF_CONTAINER_CLASSNAME} />
        </div>
      )} */}
      {showTelegram && (
        <AuthFormBtn
          type='button'
          disabled={isActionDisabled}
          onClick={onTelegramClick}
          className={twcls(
            'AuthFormBtn--telegram',
            showPartnerLogin && 'AuthFormBtn--last'
          )}
        >
          Код в Telegram
        </AuthFormBtn>
      )}
      {showPartnerLogin && (
        <div className='AuthForm__partner-login'>
          <div className='AuthForm__partner-login-title'>Войти с помощью</div>
          <div className='AuthForm__partner-login-icons'>
            {authMethods.redirects.map(id => {
              if (id === 'tinkoff') {
                return <div key={id} className={TINKOFF_ICON_CLASSNAME} />;
              }
              if (id === 'max') {
                return (
                  <button
                    key={id}
                    type='button'
                    className='AuthForm__max-login'
                    onClick={onMaxClick}
                    aria-label='Войти через MAX'
                  >
                    <img
                      src={maxLogo}
                      srcSet={`${maxLogo} 1x, ${maxLogo2x} 2x, ${maxLogo3x} 3x`}
                      alt=''
                    />
                  </button>
                );
              }
              return null;
            })}
          </div>
        </div>
      )}
      <AuthFormPolicyDisclaimer btnTitle={btnTitle} />
    </form>
  );
};

export default observer(Main);
